import { type FC, useCallback } from 'react';
import EditableInput from '../../../EditableInput/EditableInput';
import {
  MEETING_ID_MAX_LENGTH,
  MEETING_ID_MIN_LENGTH,
  MEETING_TITLE_MAX_LENGTH,
  MEETING_TITLE_MIN_LENGTH,
  PASSCODE_MAX_LENGTH,
} from '../../helpers/constants';
import {
  checkMeetingIdAvailability,
  validateMeetingTitle,
  validatePasscode,
} from '../../helpers/validators';
import { useMeetingDetailsHandlers } from '../../hooks';
import type { BaseTabProps } from '../../types';
import DefaultMeetingToggle from '../form-fields/DefaultMeetingToggle';

const MeetingDetailsTab: FC<BaseTabProps> = ({
  selectedMeeting,
  selectedMeetingData,
  isUpdating,
  updateMeetingData,
  toggleDefaultMeeting,
  isHostControlMode = false,
}) => {
  const { handleMeetingTitleSave, handleMeetingIdSave, handlePasscodeSave, handleDefaultToggle } =
    useMeetingDetailsHandlers({ updateMeetingData, toggleDefaultMeeting });

  const handleMeetingIdValidate = useCallback(
    async (value: string) => {
      return await checkMeetingIdAvailability(value, selectedMeeting.room);
    },
    [selectedMeeting.room],
  );

  return (
    <div className="personal-meetings-modal__tab-content">
      <EditableInput
        className="personal-meetings-modal__field-input"
        title="Meeting Title"
        value={selectedMeetingData.meetingTitle}
        minInput={MEETING_TITLE_MIN_LENGTH}
        maxInput={MEETING_TITLE_MAX_LENGTH}
        disabled={isUpdating}
        info={false}
        validationInfo={false}
        showEditControl={true}
        onSave={handleMeetingTitleSave}
        onValidate={validateMeetingTitle}
      />

      <div className="personal-meetings-modal__form-field personal-meetings-modal__form-field--row">
        <div className="personal-meetings-modal__form-group">
          <EditableInput
            className="personal-meetings-modal__field-input"
            title="Meeting ID"
            info={false}
            validationInfo={!isHostControlMode}
            minInput={MEETING_ID_MIN_LENGTH}
            maxInput={MEETING_ID_MAX_LENGTH}
            placeholder="Enter meeting id"
            value={selectedMeetingData.meetingId}
            disabled={isUpdating || isHostControlMode}
            showEditControl={!isHostControlMode}
            onSave={handleMeetingIdSave}
            onValidate={handleMeetingIdValidate}
            debounceMs={500}
          />
        </div>
        <div className="personal-meetings-modal__form-group">
          <EditableInput
            className="personal-meetings-modal__field-input"
            title="Passcode"
            info={false}
            validationInfo
            maxInput={PASSCODE_MAX_LENGTH}
            placeholder="Enter passcode"
            value={selectedMeetingData.passcode}
            disabled={isUpdating}
            onSave={handlePasscodeSave}
            onValidate={validatePasscode}
          />
        </div>
      </div>

      {!isHostControlMode && (
        <DefaultMeetingToggle
          checked={selectedMeetingData.isDefaultMeeting}
          disabled={isUpdating}
          onChange={handleDefaultToggle}
        />
      )}
    </div>
  );
};

export default MeetingDetailsTab;
